import { Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CnicInput from "../../components/CnicInput";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import { uploadFiles } from "../../store/Slices/FileSlice";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/Input.css";
import "../../styles/Issue.css";
import "../../styles/Trip.css";
import "../../styles/DriverSafety.css";
import { refreshIcon } from "../../utils/svgs";
import {
  createDriver,
  generateDriverPassword,
} from "../../store/Slices/DriverSlice";
import { fetchUsersDropdown } from "../../store/Slices/dropdownSlice";
import { formats, modules } from "../../utils/helper";
import { successNotify, errorNotify } from "../../utils/toast";
import { driverFormSchema } from "./addDriver.schema";
import { apiService } from "../../services/apiService";
import { LuPencil, LuTrash2 } from "react-icons/lu";

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const AUTH_METHOD_OPTIONS = [
  { label: "QR Code + OTP (Primary)", value: "qr_otp_primary" },
  { label: "OTP Only", value: "otp_only" },
];

const SALARY_TERMS_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Weekly", value: "weekly" },
];

const ALLOWANCE_TYPE_OPTIONS = [
  { label: "Food Allowance", value: "food" },
  { label: "TA/DA", value: "tada" },
  { label: "Trip Allowance", value: "trip" },
  { label: "Mobile Allowance", value: "mobile" },
  { label: "Arrears", value: "arrears" },
];

const ALLOWANCE_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Monthly", value: "monthly" },
  { label: "Occasionally", value: "occasionally" },
];

const ALLOWANCE_TYPE_LABELS = {
  food: "Food Allowance",
  tada: "TA/DA",
  trip: "Trip Allowance",
  mobile: "Mobile Allowance",
  arrears: "Arrears",
};

const EMPTY_ALLOWANCE_FORM = {
  editingId: null,
  allowanceType: "",
  amount: "",
  frequency: "",
};

const PROFILE_PICTURE_MAX_SIZE_BYTES = 2 * 1024 * 1024;
const DEFAULT_AUTH_METHOD = AUTH_METHOD_OPTIONS[0].value;
const DEFAULT_SALARY_TERM = SALARY_TERMS_OPTIONS[0].value;

const toDigits = (value) => String(value || "").replace(/\D/g, "");
const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const isDirectAssetUrl = (value) => /^(https?:)?\/\//i.test(String(value || "").trim());
const isSpecialAssetUrl = (value) => /^(blob:|data:)/i.test(String(value || "").trim());

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u200B/g, "")
    .replace(/\s+/g, " ")
    .trim();

const getDriverIdFromEmployeeNumber = (employeeNumber) => {
  const digits = toDigits(employeeNumber);
  return digits ? `DRV-${digits.slice(-6).padStart(6, "0")}` : "";
};

const formatLastActivity = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toInputDate = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue.slice(0, 10);
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const resolveDriverImageSrc = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (isDirectAssetUrl(rawValue) || isSpecialAssetUrl(rawValue)) return rawValue;
  return `${process.env.REACT_APP_API_FILES_URL}${rawValue}`;
};

const buildDriverFormValues = (
  driverRecord,
  fallbackEmployeeNumber = "",
  fallbackAuthCode = ""
) => ({
  driverName: driverRecord?.driverName || "",
  employeeNumber: driverRecord?.employeeNumber || fallbackEmployeeNumber,
  cnicNumber: driverRecord?.cnicNumber || "",
  contact: toDigits(driverRecord?.contactNumber).slice(0, 12),
  dateOfBirth: toInputDate(driverRecord?.dateOfBirth || driverRecord?.date_of_birth),
  cnicIssueDate: toInputDate(driverRecord?.cnicIssueDate || driverRecord?.cnic_issue_at),
  cnicExpiryDate: toInputDate(driverRecord?.cnicExpiryDate || driverRecord?.cnic_expire_at),
  licenseNumber: driverRecord?.licenseNumber || "",
  licenseIssueDate: toInputDate(
    driverRecord?.licenseIssueDate || driverRecord?.license_issue_at
  ),
  licenseExpiryDate: toInputDate(
    driverRecord?.licenseExpiryDate || driverRecord?.license_expire_at
  ),
  appAuthCode: driverRecord?.appAuthCode || fallbackAuthCode,
  authMethod: driverRecord?.authMethod || DEFAULT_AUTH_METHOD,
  baseSalary: String(driverRecord?.baseSalary || ""),
  salaryTerms: driverRecord?.salaryTerms || DEFAULT_SALARY_TERM,
  casualLeaveQuota: String(driverRecord?.casualLeaveQuota || ""),
  annualLeaveQuota: String(driverRecord?.annualLeaveQuota || ""),
  sickLeaveQuota: String(driverRecord?.sickLeaveQuota || ""),
  paternityLeaveQuota: String(driverRecord?.paternityLeaveQuota || ""),
  supervisorId: driverRecord?.supervisorId ?? driverRecord?.supervisor_id ?? driverRecord?.supervisor?.id ?? "",
  attachments: normalizeArray(driverRecord?.attachments),
  notes: driverRecord?.notes || "",
});

const buildDriverRecordFromValues = ({
  values,
  currentDriver = null,
  profileImage = "",
  historyItems = null,
}) => {
  const employeeNumber = values?.employeeNumber || currentDriver?.employeeNumber || "";
  const existingId = currentDriver?.id;
  const baseSalary = Number(values?.baseSalary || 0);
  const deductions = Number(currentDriver?.costSummary?.deductions || 0);

  return {
    id: existingId || Date.now(),
    userId: currentDriver?.userId || "",
    driverId: currentDriver?.driverId || getDriverIdFromEmployeeNumber(employeeNumber),
    driverName: String(values?.driverName || "").trim(),
    employeeNumber,
    cnicNumber: String(values?.cnicNumber || "").trim(),
    contactNumber: `+${toDigits(values?.contact).slice(0, 12)}`,
    dateOfBirth: values?.dateOfBirth || "",
    cnicIssueDate: values?.cnicIssueDate || "",
    cnicExpiryDate: values?.cnicExpiryDate || "",
    licenseNumber: String(values?.licenseNumber || "").trim(),
    licenseIssueDate: values?.licenseIssueDate || "",
    licenseExpiryDate: values?.licenseExpiryDate || "",
    appAuthCode: String(values?.appAuthCode || "").trim(),
    authMethod: values?.authMethod || DEFAULT_AUTH_METHOD,
    baseSalary: String(values?.baseSalary || ""),
    salaryTerms: values?.salaryTerms || DEFAULT_SALARY_TERM,
    casualLeaveQuota: String(values?.casualLeaveQuota || ""),
    annualLeaveQuota: String(values?.annualLeaveQuota || ""),
    sickLeaveQuota: String(values?.sickLeaveQuota || ""),
    paternityLeaveQuota: String(values?.paternityLeaveQuota || ""),
    supervisorId: values?.supervisorId ?? currentDriver?.supervisorId ?? "",
    status: currentDriver?.status || "Active",
    safetyScore: Number(currentDriver?.safetyScore || 100),
    lastActivity: formatLastActivity(new Date().toISOString()),
    assignedTrips: Number(currentDriver?.assignedTrips ?? 0),
    notes: values?.notes || "",
    image: profileImage || currentDriver?.image || "",
    attachments: normalizeArray(values?.attachments),
    historyItems: normalizeArray(historyItems || currentDriver?.historyItems),
    costSummary: {
      tripExpenses: Number(currentDriver?.costSummary?.tripExpenses || 0),
      refuelling: Number(currentDriver?.costSummary?.refuelling || 0),
      deductions,
      totalNetSalary: Math.max(baseSalary - deductions, 0),
    },
    expenseLogs: normalizeArray(currentDriver?.expenseLogs),
    attendanceLogs: normalizeArray(currentDriver?.attendanceLogs),
    leaveLogs: normalizeArray(currentDriver?.leaveLogs),
    payrollLogs: normalizeArray(currentDriver?.payrollLogs),
    trainingLogs: normalizeArray(currentDriver?.trainingLogs),
    trainingScore: Number(currentDriver?.trainingScore ?? currentDriver?.safetyScore ?? 0),
  };
};

const resolveUploadedDriverImagePath = (uploadResponse) => {
  const queue = Array.isArray(uploadResponse) ? [...uploadResponse] : [uploadResponse];
  const visited = new Set();

  while (queue.length) {
    const current = queue.shift();

    if (current === null || current === undefined || current === "") {
      continue;
    }

    if (typeof current === "string") {
      const normalizedValue = current.trim();
      if (normalizedValue && !normalizedValue.startsWith("blob:")) {
        return normalizedValue;
      }
      continue;
    }

    if (typeof current !== "object" || visited.has(current)) {
      continue;
    }
    visited.add(current);

    const directPath =
      current?.file_path ||
      current?.fileName ||
      current?.file_name ||
      current?.file_url ||
      current?.full_url ||
      current?.media_url ||
      current?.path ||
      current?.url ||
      current?.src ||
      current?.document ||
      current?.file ||
      current?.image ||
      (typeof current?.data === "string" ? current.data : "");

    if (String(directPath || "").trim()) {
      return String(directPath).trim();
    }

    [
      current?.data,
      current?.files,
      current?.results,
      current?.items,
      current?.rows,
      current?.media,
      current?.attachments,
      current?.uploadedFiles,
      current?.uploaded_files,
      ...Object.values(current),
    ].forEach((item) => {
      if (item && typeof item === "object") {
        if (Array.isArray(item)) {
          queue.push(...item);
        } else {
          queue.push(item);
        }
      } else if (typeof item === "string" && item.trim()) {
        queue.push(item.trim());
      }
    });
  }

  return "";
};

const buildDriverCreatePayload = (driverRecord) => {
  const resolvedImage = String(driverRecord?.image || "");
  const uploadedFiles = normalizeArray(driverRecord?.attachments)
    .map((file) => resolveUploadedDriverImagePath(file))
    .filter(Boolean);

  return {
    image:
      resolvedImage && !resolvedImage.startsWith("blob:")
        ? resolvedImage
        : "",
    user_name: String(driverRecord?.driverName || "").trim(),
    contact: String(driverRecord?.contactNumber || "").trim(),
    employee_id: String(driverRecord?.employeeNumber || "").trim(),
    date_of_birth: driverRecord?.dateOfBirth || "",
    cnic: String(driverRecord?.cnicNumber || "").trim(),
    cnic_issue_at: driverRecord?.cnicIssueDate || "",
    cnic_expire_at: driverRecord?.cnicExpiryDate || "",
    license_number: String(driverRecord?.licenseNumber || "").trim(),
    license_issue_at: driverRecord?.licenseIssueDate || "",
    license_expire_at: driverRecord?.licenseExpiryDate || "",
    basic_salary: Number(driverRecord?.baseSalary || 0),
    salary_term: driverRecord?.salaryTerms || DEFAULT_SALARY_TERM,
    casual_leave: Number(driverRecord?.casualLeaveQuota || 0),
    annual_leave: Number(driverRecord?.annualLeaveQuota || 0),
    sick_leave: Number(driverRecord?.sickLeaveQuota || 0),
    paternity_leave: Number(driverRecord?.paternityLeaveQuota || 0),
    safety_score: Number(driverRecord?.safetyScore || 100),
    notes: stripHtml(driverRecord?.notes || ""),
    auth_method: String(driverRecord?.authMethod || DEFAULT_AUTH_METHOD).trim(),
    auth_code: String(driverRecord?.appAuthCode || "").trim(),
    supervisor_id: driverRecord?.supervisorId ? Number(driverRecord.supervisorId) : null,
    files: uploadedFiles,
  };
};

const AddDriver = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const profileInputRef = useRef(null);
  const editingDriver = location.state?.driver || null;
  const isEditing = Boolean(editingDriver?.driverId);
  const { createDriverLoading, generateDriverPasswordLoading } = useSelector(
    (state) => state.drivers || {}
  );
  const { usersDropdownData = [] } = useSelector(
    (state) => state.dropdowns || {}
  );

  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [allowances, setAllowances] = useState([]);
  const [allowanceForm, setAllowanceForm] = useState(EMPTY_ALLOWANCE_FORM);
  const [allowanceSaving, setAllowanceSaving] = useState(false);
  const [allowanceErrors, setAllowanceErrors] = useState({});

  useEffect(() => {
    dispatch(fetchUsersDropdown());
  }, [dispatch]);

  const userOptions = useMemo(() => {
    const list = Array.isArray(usersDropdownData) ? usersDropdownData : [];
    return list
      .map((user) => {
        const rawValue = user?.value ?? user?.id;
        const rawLabel =
          user?.label ??
          user?.name ??
          user?.user_name ??
          user?.username ??
          user?.email ??
          (rawValue ? `User #${rawValue}` : "");
        return {
          ...user,
          label: String(rawLabel || "").trim(),
          value: rawValue,
        };
      })
      .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== "");
  }, [usersDropdownData]);

  useEffect(() => {
    return () => {
      if (String(profilePreviewUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(profilePreviewUrl);
      }
    };
  }, [profilePreviewUrl]);

  useEffect(() => {
    if (editingDriver?.image) {
      updateProfilePreview(editingDriver.image);
      return;
    }

    setProfilePicture(null);
    updateProfilePreview("");
  }, [editingDriver]);

  const initialValues = useMemo(() => {
    const defaultValues = buildDriverFormValues(editingDriver);

    if (isEditing) {
      return defaultValues;
    }

    return {
      ...defaultValues,
      employeeNumber: "",
      appAuthCode: "",
      authMethod: "",
      baseSalary: "",
      salaryTerms: "",
      casualLeaveQuota: "",
      annualLeaveQuota: "",
      sickLeaveQuota: "",
      paternityLeaveQuota: "",
    };
  }, [editingDriver, isEditing]);

  const updateProfilePreview = (nextUrl) => {
    setProfilePreviewUrl((previousUrl) => {
      if (String(previousUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }
      return nextUrl;
    });
  };

  const handleProfilePictureChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    if (!String(selectedFile.type || "").startsWith("image/")) {
      errorNotify("Only image files are allowed for profile picture");
      event.target.value = "";
      return;
    }

    if (selectedFile.size > PROFILE_PICTURE_MAX_SIZE_BYTES) {
      errorNotify("Profile picture must be under 2 MB");
      event.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setProfilePicture(selectedFile);
    updateProfilePreview(objectUrl);
    event.target.value = "";
  };

  const handleRemoveProfilePicture = () => {
    setProfilePicture(null);
    updateProfilePreview("");
  };

  const handleGenerateDriverPassword = async (setFieldValue) => {
    try {
      const response = await dispatch(generateDriverPassword()).unwrap();
      const generatedPassword = String(response?.data ?? response ?? "").trim();

      if (!generatedPassword) {
        throw "Driver password generation failed";
      }

      setFieldValue("appAuthCode", generatedPassword);
    } catch (error) {
      errorNotify(error);
    }
  };

  const handleAddAllowance = () => {
    const { editingId, allowanceType, amount, frequency } = allowanceForm;
    const errs = {};
    if (!allowanceType) errs.allowanceType = "Please select an allowance type";
    if (!amount) errs.amount = "Please enter the amount";
    if (!frequency) errs.frequency = "Please select a frequency";
    if (Object.keys(errs).length) { setAllowanceErrors(errs); return; }
    setAllowanceErrors({});

    const entry = {
      id: editingId || null,
      _localId: editingId || Date.now(),
      allowance_type: allowanceType,
      amount: Number(amount),
      frequency,
    };

    if (editingId) {
      setAllowances((prev) => prev.map((a) => a._localId === editingId ? entry : a));
    } else {
      setAllowances((prev) => [...prev, { ...entry, _localId: Date.now() }]);
    }

    setAllowanceForm(EMPTY_ALLOWANCE_FORM);
  };

  const handleEditAllowance = (item) => {
    setAllowanceForm({
      editingId: item._localId,
      allowanceType: item.allowance_type,
      amount: String(item.amount),
      frequency: item.frequency,
    });
  };

  const handleDeleteAllowance = (localId) => {
    setAllowances((prev) => prev.filter((a) => a._localId !== localId));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let resolvedProfileImage = profilePreviewUrl;

      if (profilePicture) {
        const formData = new FormData();
        formData.append("media", profilePicture);
        formData.append("destination", "driver-safety");

        const uploadResponse = await dispatch(uploadFiles(formData)).unwrap();
        const uploadedImagePath = resolveUploadedDriverImagePath(uploadResponse);

        if (!uploadedImagePath) {
          throw "Profile picture upload failed";
        }

        resolvedProfileImage = uploadedImagePath;
      }

      const createdDriver = buildDriverRecordFromValues({
        values,
        currentDriver: editingDriver,
        profileImage: resolvedProfileImage,
      });

      if (!isEditing) {
        const payload = buildDriverCreatePayload(createdDriver);
        const createResponse = await dispatch(createDriver(payload)).unwrap();
        const newDriverInfoId = createResponse?.data?.id;
        if (newDriverInfoId && allowances.length > 0) {
          await apiService.saveDriverAllowances(newDriverInfoId, allowances.map(({ _localId, ...a }) => a));
        }
      }

      successNotify(isEditing ? "Driver updated successfully" : "Driver created successfully");
      navigate("/driver-safety");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={driverFormSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setValues,
        handleSubmit,
        isSubmitting,
      }) => {
        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function"
              ? updater(values.attachments || [])
              : updater;
          setFieldValue("attachments", nextFiles);
        };

        return (
          <div className="create_vehicle_page add_driver_page">
            <Breadcrumbs values={["Operations", "Driver", "Add Driver"]} />


            <div className="page_header access_view">
              <h2>{isEditing ? "Edit Driver" : "Add Driver"}</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/driver-safety")}
                  submitBtn={handleSubmit}
                  submitText={
                    isSubmitting || (createDriverLoading && !isEditing)
                      ? "Saving..."
                      : "Save"
                  }
                  type="button"
                  shouldRow={false}
                  loading={isSubmitting || (createDriverLoading && !isEditing)}
                  disabled={isSubmitting || (createDriverLoading && !isEditing)}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout">
                <Col lg={8}>
                  <div className="trip_information_main_div driver_form_shell">
                    <h5>Driver Addition Form</h5>

                    <div className="driver_form_content">
                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>General Information</h3>
                          <p>Main information for this driver record.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Driver Name")}
                              type="text"
                              placeholder="Enter Name"
                              name="driverName"
                              value={values.driverName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.driverName}
                              touched={touched.driverName}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Employee Number")}
                              type="text"
                              placeholder="Enter Employee Number"
                              name="employeeNumber"
                              value={values.employeeNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.employeeNumber}
                              touched={touched.employeeNumber}
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Driver Supervisor")}
                              placeholder="Select Driver Supervisor"
                              options={userOptions}
                              value={
                                userOptions.find(
                                  (opt) => String(opt.value) === String(values.supervisorId)
                                ) || null
                              }
                              name="supervisorId"
                              onChange={(option) =>
                                setFieldValue(
                                  "supervisorId",
                                  option ? option.value : ""
                                )
                              }
                              onBlur={handleBlur}
                              error={errors.supervisorId}
                              touched={touched.supervisorId}
                              isClearable={true}
                            />
                          </Col>
                          <Col md={6}>
                            <CnicInput
                              label={renderFieldLabel("CNIC Number")}
                              name="cnicNumber"
                              value={values.cnicNumber}
                              onBlur={handleBlur}
                              error={errors.cnicNumber}
                              touched={touched.cnicNumber}
                              setFieldValue={setFieldValue}
                            />
                          </Col>
                          <Col md={6}>
                            <PhoneNumberInput
                              label={renderFieldLabel("Contact")}
                              placeholder="923XXXXXXXXX"
                              name="contact"
                              value={values.contact}
                              onBlur={handleBlur}
                              error={errors.contact}
                              touched={touched.contact}
                              setFieldValue={setFieldValue}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Date of Birth")}
                              type="date"
                              name="dateOfBirth"
                              value={values.dateOfBirth}
                              onChange={(e) => {
                                const val = e.target.value;
                                setValues({
                                  ...values,
                                  dateOfBirth: val,
                                });
                              }}
                              onBlur={handleBlur}
                              error={errors.dateOfBirth}
                              touched={touched.dateOfBirth}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("CNIC Issue Date")}
                              type="date"
                              name="cnicIssueDate"
                              value={values.cnicIssueDate}
                              onChange={(e) => {
                                const val = e.target.value;
                                let calculatedExpiry = values.cnicExpiryDate;
                                if (val) {
                                  const dateObj = new Date(val);
                                  if (!isNaN(dateObj.getTime())) {
                                    dateObj.setFullYear(dateObj.getFullYear() + 10);
                                    const y = dateObj.getFullYear();
                                    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
                                    const d = String(dateObj.getDate()).padStart(2, "0");
                                    calculatedExpiry = `${y}-${m}-${d}`;
                                  }
                                }
                                setValues({
                                  ...values,
                                  cnicIssueDate: val,
                                  cnicExpiryDate: calculatedExpiry,
                                });
                              }}
                              onBlur={handleBlur}
                              error={errors.cnicIssueDate}
                              touched={touched.cnicIssueDate}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("CNIC Expiry Date")}
                              type="date"
                              name="cnicExpiryDate"
                              value={values.cnicExpiryDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.cnicExpiryDate}
                              touched={touched.cnicExpiryDate}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("License Number")}
                              type="text"
                              placeholder="Enter License Number"
                              name="licenseNumber"
                              value={values.licenseNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.licenseNumber}
                              touched={touched.licenseNumber}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("License Issue Date")}
                              type="date"
                              name="licenseIssueDate"
                              value={values.licenseIssueDate}
                              onChange={(e) => {
                                const val = e.target.value;
                                let calculatedExpiry = values.licenseExpiryDate;
                                if (val) {
                                  const dateObj = new Date(val);
                                  if (!isNaN(dateObj.getTime())) {
                                    dateObj.setFullYear(dateObj.getFullYear() + 5);
                                    const y = dateObj.getFullYear();
                                    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
                                    const d = String(dateObj.getDate()).padStart(2, "0");
                                    calculatedExpiry = `${y}-${m}-${d}`;
                                  }
                                }
                                setValues({
                                  ...values,
                                  licenseIssueDate: val,
                                  licenseExpiryDate: calculatedExpiry,
                                });
                              }}
                              onBlur={handleBlur}
                              error={errors.licenseIssueDate}
                              touched={touched.licenseIssueDate}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("License Expiry Date")}
                              type="date"
                              name="licenseExpiryDate"
                              value={values.licenseExpiryDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.licenseExpiryDate}
                              touched={touched.licenseExpiryDate}
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>App Authentication</h3>
                          <p>Access and sign-in settings for the driver app.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("App Auth Code")}
                              type="text"
                              placeholder="Enter Auth Code"
                              name="appAuthCode"
                              value={values.appAuthCode}
                              onBlur={handleBlur}
                              error={errors.appAuthCode}
                              touched={touched.appAuthCode}
                              disabled
                              showIcon={refreshIcon}
                              onIconClick={
                                generateDriverPasswordLoading
                                  ? null
                                  : () => handleGenerateDriverPassword(setFieldValue)
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Auth Method")}
                              placeholder="Select auth method"
                              options={AUTH_METHOD_OPTIONS}
                              loading={false}
                              value={
                                AUTH_METHOD_OPTIONS.find(
                                  (option) => option.value === values.authMethod
                                ) || null
                              }
                              name="authMethod"
                              onChange={(option) =>
                                setFieldValue("authMethod", option ? option.value : "")
                              }
                              onBlur={handleBlur}
                              error={errors.authMethod}
                              touched={touched.authMethod}
                              isSearch={false}
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Human Resources</h3>
                          <p>Compensation and leave quota details for this driver.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Base Salary")}
                              type="number"
                              placeholder="00"
                              name="baseSalary"
                              value={values.baseSalary}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.baseSalary}
                              touched={touched.baseSalary}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Salary Terms")}
                              placeholder="Select salary terms"
                              options={SALARY_TERMS_OPTIONS}
                              loading={false}
                              value={
                                SALARY_TERMS_OPTIONS.find(
                                  (option) => option.value === values.salaryTerms
                                ) || null
                              }
                              name="salaryTerms"
                              onChange={(option) =>
                                setFieldValue("salaryTerms", option ? option.value : "")
                              }
                              onBlur={handleBlur}
                              error={errors.salaryTerms}
                              touched={touched.salaryTerms}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Casual Leave Quota")}
                              type="number"
                              placeholder="0"
                              name="casualLeaveQuota"
                              value={values.casualLeaveQuota}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.casualLeaveQuota}
                              touched={touched.casualLeaveQuota}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Annual Leave Quota")}
                              type="number"
                              placeholder="0"
                              name="annualLeaveQuota"
                              value={values.annualLeaveQuota}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.annualLeaveQuota}
                              touched={touched.annualLeaveQuota}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Sick Leave Quota")}
                              type="number"
                              placeholder="0"
                              name="sickLeaveQuota"
                              value={values.sickLeaveQuota}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.sickLeaveQuota}
                              touched={touched.sickLeaveQuota}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Paternity Leave Quota")}
                              type="number"
                              placeholder="0"
                              name="paternityLeaveQuota"
                              value={values.paternityLeaveQuota}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.paternityLeaveQuota}
                              touched={touched.paternityLeaveQuota}
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Driver Allowances</h3>
                          <p>Set allowance rates for this driver. Mark eligible dates during attendance.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Allowance Type", true)}
                              placeholder="Select type"
                              options={ALLOWANCE_TYPE_OPTIONS}
                              value={ALLOWANCE_TYPE_OPTIONS.find((o) => o.value === allowanceForm.allowanceType) || null}
                              onChange={(opt) => { setAllowanceForm((f) => ({ ...f, allowanceType: opt ? opt.value : "" })); setAllowanceErrors((e) => ({ ...e, allowanceType: "" })); }}
                              isSearch={false}
                            />
                            {allowanceErrors.allowanceType && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.allowanceType}</p>}
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Amount (PKR)", true)}
                              type="number"
                              placeholder="0"
                              value={allowanceForm.amount}
                              onChange={(e) => { setAllowanceForm((f) => ({ ...f, amount: e.target.value })); setAllowanceErrors((e) => ({ ...e, amount: "" })); }}
                            />
                            {allowanceErrors.amount && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.amount}</p>}
                          </Col>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Frequency", true)}
                              placeholder="Select frequency"
                              options={ALLOWANCE_FREQUENCY_OPTIONS}
                              value={ALLOWANCE_FREQUENCY_OPTIONS.find((o) => o.value === allowanceForm.frequency) || null}
                              onChange={(opt) => { setAllowanceForm((f) => ({ ...f, frequency: opt ? opt.value : "" })); setAllowanceErrors((e) => ({ ...e, frequency: "" })); }}
                              isSearch={false}
                            />
                            {allowanceErrors.frequency && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.frequency}</p>}
                          </Col>
                          <Col md={12} className="d-flex justify-content-end">
                            <button
                              type="button"
                              onClick={handleAddAllowance}
                              className="driver_allowance_add_btn"
                            >
                              <FiPlus style={{ fontSize: "14px" }} />
                              {allowanceForm.editingId ? " Update Allowance" : " Add Allowance"}
                            </button>
                          </Col>
                        </Row>

                        {allowances.length > 0 && (
                          <div className="driver_allowances_table_wrapper">
                            <h6 className="driver_allowances_registered_title">Registered Allowances</h6>
                            <table className="driver_allowances_table">
                              <thead>
                                <tr>
                                  <th>Allowance</th>
                                  <th>Amount (PKR)</th>
                                  <th>Frequency</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allowances.map((item) => (
                                  <tr key={item._localId}>
                                    <td>{ALLOWANCE_TYPE_LABELS[item.allowance_type] || item.allowance_type}</td>
                                    <td>{Number(item.amount).toLocaleString()}</td>
                                    <td style={{ textTransform: "capitalize" }}>{item.frequency}</td>
                                    <td>
                                      <div className="driver_allowance_actions">
                                        <button type="button" onClick={() => handleEditAllowance(item)} className="driver_allowance_edit_btn">
                                          <LuPencil />
                                        </button>
                                        <button type="button" onClick={() => handleDeleteAllowance(item._localId)} className="driver_allowance_delete_btn">
                                          <LuTrash2 />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="driver_allowances_note">
                              Daily and occasional allowances are marked against attendance dates. Monthly allowances apply once per month.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Attachments</h3>
                          <p>Upload supporting files and required documents.</p>
                        </div>

                        <div className="issue_file_upload_wrapper">
                          <label>Attachment</label>
                          <FileUpload
                            destination="driver-safety"
                            files={values.attachments}
                            setFiles={updateAttachments}
                            maxFileSize={10}
                            maxFiles={5}
                            className="issue_file_upload create_vehicle_upload"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="driver_form_sidebar">
                    <div className="trip_notes_main_div driver_profile_card">
                      <h5>Profile Picture</h5>

                      <div className="driver_profile_card_body">
                        <p className="driver_profile_copy">
                          Please set a profile picture to complete your profile and make
                          it easier for others to recognize you.
                        </p>

                        <input
                          ref={profileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          style={{ display: "none" }}
                        />

                        <button
                          type="button"
                          className="driver_profile_preview_button"
                          onClick={() => profileInputRef.current?.click()}
                        >
                          {profilePreviewUrl ? (
                            <img
                              src={resolveDriverImageSrc(profilePreviewUrl)}
                              alt="Driver profile preview"
                              className="driver_profile_preview_image"
                            />
                          ) : (
                            <FaUserCircle className="driver_profile_preview_icon" />
                          )}
                          <span className="driver_profile_plus_badge">
                            <FiPlus />
                          </span>
                        </button>

                        <p className="driver_profile_hint">
                          Profile picture must be under 2 MB.
                        </p>

                        {profilePreviewUrl ? (
                          <div className="driver_profile_actions">
                            <button
                              type="button"
                              className="cancel_btn"
                              onClick={() => profileInputRef.current?.click()}
                            >
                              Change Picture
                            </button>
                            <button
                              type="button"
                              className="driver_profile_remove_btn"
                              onClick={handleRemoveProfilePicture}
                            >
                              Remove Picture
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="trip_notes_main_div create_vehicle_notes driver_notes_card">
                      <h5>Notes</h5>
                      <ReactQuill
                        placeholder="Notes if any..."
                        modules={modules}
                        formats={formats}
                        value={values.notes || ""}
                        onChange={(content) => setFieldValue("notes", content)}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                      {touched.notes && errors.notes ? (
                        <div
                          className="error-message"
                          style={{
                            color: "#ff0000de",
                            fontSize: "12px",
                            margin: "8px 12px 0",
                          }}
                        >
                          {errors.notes}
                        </div>
                      ) : null}
                    </div>

                    <FileUpload
                      label="Attachments"
                      files={values.attachments}
                      setFieldValue={setFieldValue}
                      error={errors.attachments}
                      touched={touched.attachments}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddDriver;